/**
 * SignatureSettings — multi-signature manager.
 *
 * Lists every practitioner signature stored in the ``signatures``
 * collection. One is flagged as the DEFAULT (used everywhere a letter or
 * SOAP export doesn't nominate a specific one). Each signature can
 * optionally set ``acuity_provider_name`` so the letter compose flow
 * auto-picks the right block based on the appointment's provider.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PenLine, Plus, Save, Trash2, Star, StarOff, Loader2, X, User,
} from "lucide-react";

const EMPTY = {
  name: "", title: "", qualifications: "", provider_number: "",
  phone: "", email: "", clinic: "", acuity_provider_name: "",
};

export default function SignatureSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // signature obj or {} for new
  const [mySignatureId, setMySignatureId] = useState(null);
  const [savingMine, setSavingMine] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [rSigs, rMine] = await Promise.all([
        api.get("/settings/signatures"),
        api.get("/settings/my-signature").catch(() => ({ data: { signature_id: null } })),
      ]);
      setRows(rSigs.data?.signatures || []);
      setMySignatureId(rMine.data?.signature_id || null);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function pickMine(id) {
    setSavingMine(true);
    try {
      const r = await api.put("/settings/my-signature", { signature_id: id });
      setMySignatureId(r.data?.signature_id || null);
      toast.success(id ? "Set as your signature" : "Using clinic default");
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || e.message });
    } finally { setSavingMine(false); }
  }

  async function setDefault(id) {
    try {
      await api.post(`/settings/signatures/${encodeURIComponent(id)}/set-default`);
      toast.success("Default signature updated");
      await load();
    } catch (e) {
      toast.error("Could not set default", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function remove(id, name) {
    if (!window.confirm(`Delete signature "${name}"? Letters already sent are unaffected.`)) return;
    try {
      await api.delete(`/settings/signatures/${encodeURIComponent(id)}`);
      toast.success("Signature deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <Card
      className="p-6 rounded-lg border border-zinc-200 shadow-none"
      data-testid="signature-settings"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <PenLine className="h-3.5 w-3.5" /> Practitioner signatures
          </div>
          <h3 className="mt-1 font-display text-2xl text-zinc-950">
            Signature blocks
          </h3>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Add one block per practitioner. The <b>default</b> is used
            everywhere a letter or SOAP export doesn&apos;t nominate a
            specific one. Set <span className="font-mono-num text-xs">Acuity
            provider name</span> to auto-pick a block based on the
            appointment&apos;s provider. Click <b>Use as mine</b> on any
            row to pick your personal signature — used by AI Draft Reply
            and Letters instead of the clinic default.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY, __new: true })}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="signature-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add signature
        </Button>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading signatures…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 border border-dashed border-zinc-300 rounded-md px-4">
            No signatures yet. Add one and it will become the default.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200 border border-zinc-200 rounded-md overflow-hidden">
            {rows.map((s) => (
              <li
                key={s.id}
                className="flex items-center gap-3 p-3 hover:bg-zinc-50"
                data-testid={`signature-row-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => !s.is_default && setDefault(s.id)}
                  className="shrink-0"
                  title={s.is_default ? "Default signature" : "Make default"}
                  data-testid={`signature-set-default-${s.id}`}
                >
                  {s.is_default
                    ? <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
                    : <StarOff className="h-4 w-4 text-zinc-400 hover:text-amber-500" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-zinc-950">{s.name || "Unnamed"}</span>
                    {s.is_default && (
                      <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-2xs uppercase tracking-wider border">
                        Default
                      </Badge>
                    )}
                    {s.title && <span className="text-xs text-zinc-500">{s.title}</span>}
                  </div>
                  <div className="text-2xs text-zinc-500 font-mono-num truncate">
                    {[s.qualifications, s.provider_number, s.email].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {s.acuity_provider_name && (
                    <div className="text-2xs text-zinc-500 mt-0.5">
                      Auto-picks when Acuity provider is <b>{s.acuity_provider_name}</b>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 items-center">
                  <button
                    type="button"
                    onClick={() => pickMine(mySignatureId === s.id ? null : s.id)}
                    disabled={savingMine}
                    className={`inline-flex items-center gap-1 text-2xs uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${
                      mySignatureId === s.id
                        ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                        : "bg-white text-zinc-600 border-zinc-300 hover:bg-emerald-50 hover:text-emerald-900 hover:border-emerald-200"
                    }`}
                    data-testid={`signature-mine-${s.id}`}
                    title={mySignatureId === s.id ? "This is your personal signature — click to unset" : "Use this as MY signature"}
                  >
                    <User className="h-3 w-3" />
                    {mySignatureId === s.id ? "Mine" : "Use as mine"}
                  </button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(s)}
                    data-testid={`signature-edit-${s.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(s.id, s.name)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`signature-delete-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <SignatureEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </Card>
  );
}


function SignatureEditor({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    if (!(form.name || "").trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name, title: form.title, qualifications: form.qualifications,
        provider_number: form.provider_number, phone: form.phone, email: form.email,
        clinic: form.clinic, acuity_provider_name: form.acuity_provider_name,
      };
      if (isNew) {
        await api.post("/settings/signatures", body);
        toast.success("Signature added");
      } else {
        await api.put(`/settings/signatures/${encodeURIComponent(initial.id)}`, body);
        toast.success("Signature updated");
      }
      await onSaved();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="signature-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isNew ? "Add signature" : "Edit signature"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name *" value={form.name} onChange={upd("name")} testId="sig-name" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Title" value={form.title} onChange={upd("title")}
              placeholder="Exercise Physiologist" testId="sig-title" />
            <Field label="Qualifications" value={form.qualifications} onChange={upd("qualifications")}
              placeholder="BSc EP (Hons), AEP" testId="sig-quals" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Provider number" value={form.provider_number} onChange={upd("provider_number")}
              placeholder="4123456B" testId="sig-provider" />
            <Field label="Phone" value={form.phone} onChange={upd("phone")}
              placeholder="+61 4xx xxx xxx" testId="sig-phone" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Email" value={form.email} onChange={upd("email")}
              placeholder="name@clinic.com.au" testId="sig-email" />
            <Field label="Clinic" value={form.clinic} onChange={upd("clinic")}
              placeholder="Living Energy Pilates & Rehab" testId="sig-clinic" />
          </div>
          <Field
            label="Acuity provider name (optional)"
            value={form.acuity_provider_name}
            onChange={upd("acuity_provider_name")}
            placeholder="Rafael Merino — exact match, used to auto-pick this signature"
            testId="sig-acuity-provider"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="signature-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {isNew ? "Add" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, placeholder = "", testId }) {
  return (
    <div>
      <Label className="text-2xs uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-1"
        data-testid={testId}
      />
    </div>
  );
}
